import React, { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle, Clock } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import emailjs from '@emailjs/browser';

const Contact = ({ personal }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();
  const { ref: contactRef, isVisible: contactVisible } = useScrollReveal();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

      const result = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          title: formData.subject,
          name: formData.name,
          email: formData.email,
          time: new Date().toLocaleString(),
          message: formData.message,
        }
      );

      if (result.status === 200 || result.text === 'OK') {
        // Send auto-reply to the sender (requires a separate template with To Email = {{to_email}})
        const autoReplyTemplateId = process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID;
        if (autoReplyTemplateId) {
          try {
            await emailjs.send(
              process.env.REACT_APP_EMAILJS_SERVICE_ID,
              autoReplyTemplateId,
              {
                to_email: formData.email,
                to_name: formData.name,
                subject: formData.subject,
              }
            );
          } catch (autoReplyError) {
            console.warn('Auto-reply failed:', autoReplyError);
          }
        }

        setSubmitted(true);
        toast({
          title: t('toast.messageSentTitle'),
          description: t('toast.messageSentDescription'),
        });

        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error(`Email sending failed with status: ${result.status}`);
      }

    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: t('toast.messageErrorTitle'),
        description: t('toast.messageErrorDescription'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="contact" className="py-20 md:py-32 bg-background dark:bg-background">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <div className="mb-8">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-light text-foreground mb-4">
              {t('contact.successTitle')}
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              {t('contact.successSubtitle')}
            </p>
            <p className="text-base text-muted-foreground/80 mb-8">
              {t('contact.successDesc')}
            </p>
            <Button
              onClick={() => setSubmitted(false)}
              variant="outline"
              className="border-border hover:border-blue-300 dark:hover:border-blue-700"
            >
              {t('contact.sendAnother')}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/3 via-background to-teal-500/3 dark:from-blue-500/5 dark:via-background dark:to-teal-500/5" />
      <div className="max-w-6xl mx-auto px-6 sm:px-8 relative z-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-medium mb-4 tracking-wide uppercase">
            {t('contact.title')}
          </span>
          <h2 className="text-3xl md:text-5xl font-light text-foreground mb-4">
            {t('contact.title')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-teal-500 mx-auto rounded-full mb-8" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div ref={contactRef} className={`grid lg:grid-cols-2 gap-12 items-start reveal-up ${contactVisible ? 'visible' : ''}`}>
          {/* Contact information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-foreground mb-6">
                {t('contact.letsConnect')}
              </h3>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {t('contact.connectDesc')}
              </p>
            </div>

            {/* Contact cards */}
            <div className="space-y-4">
              <Card className="p-4 bg-card dark:bg-card border border-border/50 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-300">
                <div className="flex items-center">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-md shadow-blue-500/20 mr-4">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">Email</h4>
                    <a
                      href={`mailto:${personal.email}`}
                      className="text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {personal.email}
                    </a>
                    {personal.emailSecondary && (
                      <>
                        <span className="text-muted-foreground/50 mx-1">|</span>
                        <a
                          href={`mailto:${personal.emailSecondary}`}
                          className="text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        >
                          {personal.emailSecondary}
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-card dark:bg-card border border-border/50 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-300">
                <div className="flex items-center">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-green-500 to-green-600 shadow-md shadow-green-500/20 mr-4">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">{t('contact.location')}</h4>
                    <span className="text-muted-foreground">{personal.location}</span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Response time */}
            <div className="bg-muted dark:bg-muted rounded-xl p-6 border border-border/50">
              <div className="flex items-center mb-2">
                <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                <h4 className="font-medium text-foreground">{t('contact.responseTime')}</h4>
              </div>
              <p className="text-muted-foreground text-sm">
                {t('contact.responseTimeDesc')}
              </p>
            </div>
          </div>

          {/* Contact form */}
          <Card className="p-8 bg-card dark:bg-card border border-border/50 focus-within:border-blue-200 dark:focus-within:border-blue-800 focus-within:shadow-lg focus-within:shadow-blue-500/5 transition-all duration-300">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                    {t('contact.nameLabel')} *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.namePlaceholder')}
                    className="border-border focus:border-blue-500 focus:ring-blue-500 bg-background dark:bg-background"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    {t('contact.emailLabel')} *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.emailPlaceholder')}
                    className="border-border focus:border-blue-500 focus:ring-blue-500 bg-background dark:bg-background"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
                  {t('contact.subjectLabel')} *
                </label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t('contact.subjectPlaceholder')}
                  className="border-border focus:border-blue-500 focus:ring-blue-500 bg-background dark:bg-background"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  {t('contact.messageLabel')} *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.messagePlaceholder')}
                  rows={5}
                  className="border-border focus:border-blue-500 focus:ring-blue-500 resize-none bg-background dark:bg-background"
                  disabled={isSubmitting}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {t('contact.sending')}
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Send className="w-4 h-4 mr-2" />
                    {t('contact.sendMessage')}
                  </div>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
